import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import clsx from "clsx";
import React from "react";
import type { DraggableProvided } from "react-beautiful-dnd";
import { Draggable } from "react-beautiful-dnd";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { VendorUserModal } from "~/components/specifics/vendors/settings/vendor_users/VendorUserModal";
import { useIsItandi } from "~/context/VendorSession";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import { useVendorUsers } from "~/hooks/vendors/useVendorUsers";
import type { VendorUser } from "~/models/VendorUser";
import { isSuccess } from "~/utils/api/api_handler";
import { destroy } from "~/utils/api/vendors/VendorUser";
import Styles from "./VendorUserList.module.scss";

type Props = {
	index: number;
	vendorUser: VendorUser;
};

export const VendorUserListRow: React.FC<Props> = ({
	vendorUser,
	index
}: Props) => {
	const { notify } = useNotification();
	const canEditSlackMemberId = useIsItandi();
	const { data: vendorUsers, mutate: mutateVendorUsers } =
		useVendorUsers().index();
	const {
		isOpen: showUpdate,
		handleOpen: openUpdate,
		handleClose: closeUpdate
	} = useOpenHandler();
	const {
		isOpen: confirmDestroy,
		handleOpen: openConfirmDestroy,
		handleClose: closeConfirmDestroy
	} = useOpenHandler();
	const onClickEdit = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			openUpdate();
		},
		[openUpdate]
	);
	const onClickDelete = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			openConfirmDestroy();
		},
		[openConfirmDestroy]
	);
	const handleDeleteVendorUser = React.useCallback(async () => {
		const result = await destroy(vendorUser.id);
		if (isSuccess(result)) {
			await mutateVendorUsers?.();
			notify({ content: "削除しました。", variant: "success" });
			return;
		}
		notify({
			content: `エラーが発生しました。${result.message}`,
			variant: "error"
		});
	}, [mutateVendorUsers, notify, vendorUser.id]);
	if (vendorUsers === null) {
		return null;
	}
	return (
		<Draggable draggableId={String(vendorUser.id)} index={index}>
			{(provided: DraggableProvided): React.ReactElement<HTMLElement> => (
				<tr
					className={clsx(Styles.ListRow)}
					ref={provided.innerRef}
					style={provided.draggableProps.style}
					{...provided.draggableProps}
				>
					<td className={Styles.OrderHandle} {...provided.dragHandleProps}>
						<img alt="drag handle" src="/assets/DragHandle.svg" />
					</td>
					<td className={clsx(Styles.ListCol, Styles.SpCol)}>
						<div className={Styles.Name}>{vendorUser.name}</div>
						<div className={Styles.SubInfo}>{vendorUser.email}</div>
						<div className={Styles.SubInfo}>{vendorUser.tel}</div>
						{canEditSlackMemberId ? (
							<div className={Styles.SubInfo}>
								Slack ID: {vendorUser.slackMemberId ?? "未設定"}
							</div>
						) : null}
					</td>
					<td className={clsx(Styles.ListCol, Styles.PcCol)}>
						{vendorUser.name}
					</td>
					<td className={clsx(Styles.ListCol, Styles.PcCol)}>
						{vendorUser.email}
					</td>
					<td className={clsx(Styles.ListCol, Styles.PcCol)}>
						{vendorUser.tel}
					</td>
					{canEditSlackMemberId ? (
						<td className={clsx(Styles.ListCol, Styles.PcCol)}>
							{vendorUser.slackMemberId ?? "未設定"}
						</td>
					) : null}
					<td className={clsx(Styles.ListCol, Styles.Edit)}>
						<a className={Styles.EditLink} href="#" onClick={onClickEdit}>
							編集
						</a>
						{vendorUsers.length >= 1 && (
							<a className={Styles.DeleteLink} href="#" onClick={onClickDelete}>
								削除
							</a>
						)}
						{showUpdate ? (
							<VendorUserModal onClose={closeUpdate} vendorUser={vendorUser} />
						) : null}
						{confirmDestroy ? (
							<ConfirmModal
								confirmType="delete"
								handleAccept={handleDeleteVendorUser}
								handleCancel={closeConfirmDestroy}
								isOpen={confirmDestroy}
							>
								担当者を削除してよろしいですか？
							</ConfirmModal>
						) : null}
					</td>
				</tr>
			)}
		</Draggable>
	);
};
